import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form990ShcF = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 990 Schedule F: Comprehensive Guide and Instructions"
          description="Easily navigate Form 990 Schedule F with our in-depth guide. Learn what it is, how to complete it accurately, and important filing deadlines."
          keywords=""
          canonical="resources/tax-exempt/form-990-schedule-f/"
          ogType="article"
          jsonLdPricing="179.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          Form 990{' '}
          <span className="highlight-container">
            <span className="highlight">Schedule F</span>
          </span>
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          File Activities Outside the U.S. with TaxZerone
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/form-990-schedule-f/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 990 Schedule F at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <b>Purpose: Form 990 Schedule F </b>is used to report an
                        organization's{' '}
                        <b>
                          foreign activities, including foreign grants, program
                          services, fundraising, business activities,
                          investments,
                        </b>{' '}
                        and <b>offices</b> outside the United States.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <b>Who Must File?</b> Organizations that answer{' '}
                        <b>"Yes"</b> to the applicable questions in{' '}
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          IRS Form 990,
                        </Link>{' '}
                        <b>Part IV, lines 14b, 15, or 16</b>. must complete and
                        attach <b>Schedule F.</b>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <b>Reporting Thresholds:</b> Schedule F is generally
                        required if an organization has<b> more than $10,000</b>{' '}
                        in aggregate foreign activities, foreign investments
                        exceeding <b>$100,000</b>, or more than <b>$5,000</b> in
                        grants or assistance provided to foreign organizations
                        or individuals.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <b>What Is Reported?</b> Schedule F reports foreign
                        program service revenue and expenses, grants and
                        assistance, fundraising activities, business operations,
                        investments, and foreign offices or personnel.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What is Schedule F?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who must file Schedule F?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#schf" sx={{ color: '#0202ff' }}>
                          Schedule F Filing Requirements
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#choose" sx={{ color: '#0202ff' }}>
                          Choose TaxZerone to complete your Schedule F filing
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#common" sx={{ color: '#0202ff' }}>
                          Commonly Asked Questions
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What is Schedule F?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Schedule F, </b>
                  is used by an organization that files{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>{' '}
                  to provide information on its activities conducted outside the
                  United States by the organization at any time during the tax
                  year. It requires detailed reporting on foreign investments,
                  grants, activities, and other transactions.
                </Typography>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  The schedule aims to provide transparency regarding
                  international operations, ensuring that tax-exempt
                  organizations comply with IRS regulations and accurately
                  report their global activities.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="who"
                >
                  Who must file Schedule F?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Tax-exempt organizations that engage in activities or
                  financial transactions outside the United States are generally
                  required to{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form 990
                  </Link>{' '}
                  Schedule F.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations that answered “Yes” on Form 990,{' '}
                  <b>
                    Part IV,
                    <i>Checklist of Required Schedules,</i>
                  </b>{' '}
                  line 14b, 15, or 16, must complete the appropriate parts of
                  Schedule F (Form 990) and attach Schedule F (Form 990) to Form
                  990.
                </Typography>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 5,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <b>
                      Embark on a stress-free tax journey with TaxZerone – your
                      gateway to effortless filing!
                    </b>
                    <br />
                    Your journey to seamless tax compliance begins here!
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schf`}
                  >
                    E-File Your Form 990
                  </Button>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="schf"
                >
                  Schedule F Filing Requirements
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  All Section{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) organizations
                  </Link>{' '}
                  that file Form 990 must complete and attach Schedule F
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-org-img.webp" />
                  <source srcSet="/assets/images/f990-schf-org-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-org-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F showing organization details and basic information"
                    width={760}
                    height={130}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Below, we have provided Schedule F filing requirements for
                  each part.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part I - General Information on Activities Outside the United
                  States.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part1-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part1-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part1-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F Part I overview of foreign activities (lines 1–2)"
                    width={760}
                    height={131}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This asks if your organization{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    keeps proper records for grants or assistance given outside
                    the U.S.
                  </span>
                  , including who received it, how much was given, and how they
                  were chosen.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  This asks{' '}
                  <span style={{ fontWeight: 600 }}>
                    how you make sure the funds or assistance sent abroad are
                    used correctly
                  </span>
                  , such as reviewing reports or checking how the money is
                  spent.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part1-line3-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part1-line3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part1-line3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F, Part I, line 3, reporting the number of offices and employees located outside the United States."
                    width={760}
                    height={699}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This section asks you to report all activities your
                  organization carried out outside the United States during the
                  tax year, grouped by world region (for example: Africa,
                  Europe, Asia, South America, etc.).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Write to the foreign region where the activity happened.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter how many offices your organization had in that region
                  (if any).{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Enter the number of employees, agents, or contractors working
                  there (don’t include volunteers).
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Mention what you did there, such as:Grants,Program
                  services,Fundraising,Business activities, or Investments.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Complete this only if you selected program services. Briefly
                  explain what service was provided.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Total money spent for activities in that region, or</li>
                  <li>
                    Total value of investments at year-end. <br />
                    (Amounts can be rounded to the nearest $1,000.)
                  </li>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3a
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add up all the amounts you reported on Part I for each region
                  on the main form (not including continuation sheets).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3b
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Add any amounts reported on extra pages if you listed more
                  regions or activities than fit on the main form.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3c
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Combine <span style={{ fontWeight: 600 }}> 3a + 3b</span> to
                  get the{' '}
                  <span style={{ fontWeight: 600 }}>
                    overall total expenditures and investments
                  </span>{' '}
                  for all foreign activities.{' '}
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Even if your organization didn’t spend money
                  abroad, you must still report any foreign activity that
                  generated over $10,000 in revenue.
                </Alert>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part II - Grants and Other Assistance to Organizations or
                  Entities Outside the United States.
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part2-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part2-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part2-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F Part II - Grants and Other Assistance to Organizations or Entities Outside the United States. Line 1 to Line 3"
                    width={760}
                    height={488}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.8,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report each recipient organization that got more than $5,000
                  from your organization during the year. Include cash and
                  noncash grants, no matter the source of the funds. List each
                  recipient on a separate line.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Write the full name of the recipient organization.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Include the recipient’s tax status (like{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3)
                  </Link>
                  ) and their Employer Identification Number.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Indicate the foreign region where the recipient’s main office
                  is, or where the grant funds are used.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Explain exactly what this money is for, like a scholarship,
                  emergency relief, or a specific project.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  State the total amount of actual money given to the recipient.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Describe the method used to send the funds, such as a direct
                  check, wire transfer, or a deposit to a school account.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you give items instead of money (like equipment or
                  supplies), list what those items are worth.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Briefly describe the specific items or services provided as
                  noncash.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (i)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Note whether you used the original cost (book), the current
                  market price (FMV), or a professional appraisal to decide what
                  the items were worth.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Count how many of the recipients you listed are officially
                  recognized as charities by their country, are a U.S.{' '}
                  <Link
                    href="/resources/tax-exempt/501c3-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    501(c)(3) nonprofit
                  </Link>
                  , or have a 501(c)(3) equivalency letter from a lawyer or the
                  grantee.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Count the rest of the recipients that don’t meet the above
                  criteria.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part III - Grants and Other Assistance to Individuals Outside
                  the United States
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part3-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part3-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part3-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F Part III - Grants and Other Assistance to Individuals Outside the United States"
                    width={760}
                    height={496}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Use this section to report any international support over
                  $5,000. This includes direct payments to individuals abroad or
                  money sent to foreign organizations specifically meant to help
                  certain people.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (a)
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Say exactly what the help was for, like scholarships, food,
                  clothing, medical supplies, shelter, disaster relief, or
                  school materials.
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (b)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Name the <span style={{ fontWeight: 600 }}> region</span>{' '}
                  where the recipients live, e.g., Africa, Asia, and South
                  America.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (c)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  How many people received this help. If you don’t know the
                  exact number, estimate and explain in Part V.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (d)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Total cash given in U.S. dollars. Includes checks, wire
                  transfers, money orders, or other payments.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (e)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Explain how the money actually got to the recipient. For
                  example: by check, bank transfer, money order, or other
                  methods. Keep it simple and clear.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (f)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Value of items or services provided instead of money.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (g)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Describe what was given, like blankets, books, medical
                  equipment, or supplies.{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 0.5, fontWeight: 600 }}>
                  Column (h)
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Explain how you decided what the noncash items were worth. For
                  example: you could say based on the current market price,
                  estimated by a professional appraiser, recorded value from our
                  books, or another reasonable way you determined the value.
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Enter in this part the information for grants and other
                  assistance made directly to foreign individuals, or directly
                  to foreign organizations for the benefit of specified foreign
                  individuals. Don&apos;t complete Part III for grants and other
                  assistance to foreign individuals through a foreign
                  organization unless the grant or assistance is earmarked for
                  the benefit of one or more specific individuals.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part IV - Foreign Forms
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part4-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part4-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part4-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F, Part IV, reporting required foreign financial accounts, investments, and related foreign filing obligations."
                    width={760}
                    height={456}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 1
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization sends property or assets to a foreign
                  corporation during the year, you may need to report this on
                  Form 926. This ensures the IRS knows about transfers of U.S.
                  property to foreign entities.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 2
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  If your organization had any interest in a foreign trust, you
                  may need to file Form 3520 or 3520-A. These forms report
                  transactions with foreign trusts and gifts received from
                  abroad. These forms are filed separately from Form 990.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 3
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization owned part of a foreign corporation, you
                  may need to file Form 5471. This reports information about
                  U.S. persons with ownership in certain foreign
                  corporations.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 4
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization was a shareholder in a passive foreign
                  investment company (PFIC) or a qualified electing fund (QEF),
                  you may need to file Form 8621. This reports on income and
                  distributions from foreign investment funds.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 5
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization owned part of a foreign partnership, you
                  may need to file Form 8865. This reports the organization’s
                  share of the partnership’s income, expenses, and other
                  activities.{' '}
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { md: 18, sm: 16, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Line 6
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If your organization conducted any operations or had dealings
                  with countries under boycott, you may need to file Form 5713.
                  This reports participation in international boycotts and is
                  filed separately from Form 990.{' '}
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: '18px !important',
                    fontWeight: 600,
                    mb: 0.5,
                    lineHeight: 1.5,
                  }}
                >
                  Part V - Supplemental Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/f990-schf-part5-img.webp" />
                  <source srcSet="/assets/images/f990-schf-part5-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/f990-schf-part5-img.png"
                    loading="lazy"
                    alt="Form 990 Schedule F, Part V, Supplemental Information section for explanations and additional details about foreign activities."
                    width={760}
                    height={418}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Part V is used to provide additional details and explanations
                  that support other parts of the form. Use it to describe:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> Part I, line 2:</span>{' '}
                    How the organization monitors grants and other assistance
                    given outside the U.S. to make sure funds are used properly.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part I, line 3, column (f):
                    </span>{' '}
                    Explain how the organization keeps track of and records its
                    expenses in its financial records.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Part II, line 1:</span>{' '}
                    How the organization records cash and noncash grants on its
                    financial statements.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Part III:</span> How the
                    organization records cash and noncash assistance to foreign
                    individuals on its financial statements.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      Part III, column (c):
                    </span>{' '}
                    How the organization estimate the number of recipients when
                    exact numbers aren’t available.
                  </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can also use Part V to explain anything else that needs
                  clarification. Always mention which part and line your
                  explanation supports, so it’s easy for the IRS to understand.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="choose"
                >
                  Choose TaxZerone to complete your Schedule F filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  We at <b>TaxZerone</b> promise more than just updates – we
                  promise instant insights into your annual information return
                  filing status, putting you in control at every turn.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  At TaxZerone, it&apos;s not just about filing; it&apos;s about
                  a seamless e-filing experience crafted just for you!
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Here’s how your Form 990 return with Schedule F attachment is
                  transmitted to the IRS in 3 simple steps!
                </Typography>
                <ol style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Provide Organization Details -</b> Choose the tax year
                    for which you want to file a return, and provide your
                    organization’s details.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Preview the Return -</b> Complete Schedule F and preview
                    the information provided in the return for accuracy before
                    transmitting.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Transmit to the IRS -</b> Transmit Schedule F and your
                    990 return to the IRS and get the acceptance in just a few
                    hours.
                  </li>
                </ol>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even if the IRS rejects your{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 990 return
                  </Link>{' '}
                  due to any reason, you can correct and retransmit it to the
                  IRS for free!
                </Typography>

                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 0 }}
                      >
                        Embrace the Future of Tax Filing – Choose TaxZerone
                        Today!
                      </Typography>
                      <Typography variant="body2">
                        Make your e-filing process simple by clicking the button
                        below.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 180, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990schf`}
                      >
                        Start E-filing Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided on this page is based on
                        guidance from the following IRS resources:
                      </Typography>
                      <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990sf.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Schedule F (Form 990) – Statement of Activities
                          Outside the United States
                        </li>{' '}
                        <li>
                          {' '}
                          <Link
                            href="https://www.irs.gov/pub/irs-pdf/i990.pdf"
                            target="_blank"
                            sx={{ color: '#0202ff' }}
                          >
                            Instructions
                          </Link>{' '}
                          for Form 990 – Return of Organization Exempt From
                          Income Tax
                        </li>{' '}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ my: 0 }}>
        <Grid
          container
          spacing={2}
          mx={2}
          direction="row"
          justifyContent="center"
          className="MuiGrid-root-width"
          sx={{ my: { xs: 0, sm: 0 } }}
        >
          <Grid
            size={{ xs: 12, sm: 10, md: 9 }}
            textAlign="left"
            my={{ xs: 2, sm: 5 }}
            mb={3}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 5,
                textAlign: 'center',
                scrollMarginTop: 83,
              }}
              id="common"
            >
              Commonly Asked Questions
            </Typography>
            <Box
              component="div"
              sx={{ mb: 5 }}
              itemScope
              itemType="https://schema.org/FAQPage"
            >
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  1. What types of activities should be reported on Schedule F?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule F requires disclosure of various international
                    activities and transactions, including foreign investments,
                    grants, contracts, partnerships, and other financial
                    dealings conducted outside the United States by the
                    organization.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  2. What is the use of filing Schedule F?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Schedule F is filed along with Form 990 exempt return to
                    provide information on its activities conducted outside the
                    United States by the organization at any time during the tax
                    year.
                  </Typography>
                </Box>
              </Box>
              <Box
                itemScope
                itemProp="mainEntity"
                itemType="https://schema.org/Question"
                sx={{
                  border: '1px solid rgba(145, 158, 171, 0.35)',
                  p: 2,
                  mb: 2,
                  backgroundColor: '#fff',
                }}
              >
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 600,
                    color: '#36324a',
                    fontSize: { lg: 20, md: 18, sm: 18, xs: 18 },
                  }}
                  itemProp="name"
                >
                  3. Are there any exceptions to the filing requirement for
                  Schedule F?
                </Typography>

                <Box
                  itemScope
                  itemProp="acceptedAnswer"
                  itemType="https://schema.org/Answer"
                >
                  <Typography component="div" itemProp="text">
                    Certain small tax-exempt organizations may be exempt from
                    filing Schedule F if they meet specific criteria outlined by
                    the IRS. However, most tax-exempt organizations with
                    international activities or financial transactions outside
                    the United States must disclose this information on Schedule
                    F to comply with IRS regulations.
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form990ShcF;
